import React, { useState } from 'react';
import { ArrowLeft, Save, RotateCcw, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import mainAreaService from '../../../services/mainAreaService';

const MainAreaForm = ({ onBack, mainAreaData = null, mode = 'add' }) => {
  // Form State
  const [formData, setFormData] = useState({
    nama: mainAreaData?.nama || '',
    ibukota: mainAreaData?.ibukota || '',
    pulau: mainAreaData?.pulau || '',
    koefisienHarga: mainAreaData?.koefisienHarga || '1.0000',
    description: mainAreaData?.description || ''
  });

  // Islands list
  const islands = [
    'Sumatera',
    'Jawa',
    'Kalimantan',
    'Sulawesi',
    'Maluku',
    'Papua',
    'Nusa Tenggara',
    'Kepulauan Riau'
  ];

  // Validation & Submit State
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle Form Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate Form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nama.trim()) {
      newErrors.nama = 'Nama provinsi harus diisi';
    }
    
    if (!formData.ibukota.trim()) {
      newErrors.ibukota = 'Ibukota harus diisi';
    }
    
    if (!formData.pulau.trim()) {
      newErrors.pulau = 'Pulau harus diisi';
    }
    
    if (!formData.koefisienHarga) {
      newErrors.koefisienHarga = 'Koefisien harga harus diisi';
    } else {
      const coefficient = parseFloat(formData.koefisienHarga);
      if (isNaN(coefficient) || coefficient < 0.0001 || coefficient > 9.9999) {
        newErrors.koefisienHarga = 'Koefisien harga harus antara 0.0001 - 9.9999';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Mohon perbaiki kesalahan pada form');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        nama: formData.nama,
        ibukota: formData.ibukota,
        pulau: formData.pulau,
        koefisienHarga: parseFloat(formData.koefisienHarga),
        description: formData.description || null
      };
      
      if (mode === 'add') {
        await mainAreaService.createMainArea(payload);
        toast.success('Main area berhasil ditambahkan!');
      } else {
        await mainAreaService.updateMainArea(mainAreaData.id, payload);
        toast.success('Main area berhasil diupdate!');
      }
      
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Error already handled by axios interceptor with toast
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Form Reset
  const handleReset = () => {
    setFormData({
      nama: '',
      ibukota: '',
      pulau: '',
      koefisienHarga: '1.0000',
      description: ''
    });
    setErrors({});
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to list"
              type="button"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {mode === 'add' ? 'Tambah Main Area' : 'Edit Main Area'}
              </h1>
              <p className="text-gray-600 mt-1">
                {mode === 'add' 
                  ? 'Isi form di bawah untuk menambah provinsi & koefisien harga' 
                  : 'Update informasi main area'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <form onSubmit={handleSubmit}>
          {/* Main Area Information Section */}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-lg font-bold text-gray-800">Informasi Main Area</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nama Provinsi */}
              <div className="md:col-span-2">
                <label htmlFor="nama" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nama Provinsi <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="nama"
                  name="nama"
                  value={formData.nama}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.nama ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., DKI Jakarta, Jawa Barat, Bali"
                  disabled={isSubmitting}
                />
                {errors.nama && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.nama}
                  </p>
                )}
              </div>

              {/* Ibukota */}
              <div>
                <label htmlFor="ibukota" className="block text-sm font-semibold text-gray-700 mb-2">
                  Ibukota <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="ibukota"
                  name="ibukota"
                  value={formData.ibukota}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.ibukota ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Jakarta, Bandung, Denpasar"
                  disabled={isSubmitting}
                />
                {errors.ibukota && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.ibukota}
                  </p>
                )}
              </div>

              {/* Pulau */}
              <div>
                <label htmlFor="pulau" className="block text-sm font-semibold text-gray-700 mb-2">
                  Pulau <span className="text-red-600">*</span>
                </label>
                <select
                  id="pulau"
                  name="pulau"
                  value={formData.pulau}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.pulau ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">Pilih Pulau</option>
                  {islands.map((island) => (
                    <option key={island} value={island}>
                      {island}
                    </option>
                  ))}
                </select>
                {errors.pulau && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.pulau}
                  </p>
                )}
              </div>

              {/* Koefisien Harga */}
              <div>
                <label htmlFor="koefisienHarga" className="block text-sm font-semibold text-gray-700 mb-2">
                  Koefisien Harga <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  id="koefisienHarga"
                  name="koefisienHarga"
                  value={formData.koefisienHarga}
                  onChange={handleChange}
                  step="0.0001"
                  min="0.0001"
                  max="9.9999"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.koefisienHarga ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., 1.0000, 0.8500, 1.1500"
                  disabled={isSubmitting}
                />
                {errors.koefisienHarga && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.koefisienHarga}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Range: 0.0001 - 9.9999 (4 decimal places)
                </p>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                  placeholder="Optional notes about this main area"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="p-6 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleReset}
                className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors flex items-center gap-2"
                disabled={isSubmitting}
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
              <button
                type="button"
                onClick={onBack}
                className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <Save className="w-5 h-5" />
                {isSubmitting ? 'Saving...' : (mode === 'add' ? 'Create Main Area' : 'Update Main Area')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MainAreaForm;